import React, { useState, useEffect } from 'react';
import { LayoutDashboard, Kanban, Calculator, Menu, X, ChevronRight, TrendingUp, Building2 } from 'lucide-react';
import DynamicCalculator from './DynamicCalculator';
import Dashboard from './Dashboard';
import Pipeline from './Pipeline';
import OpportunityForm from './OpportunityForm';
import MobileNav from './MobileNav';
import CashFlowForecast from './CashFlowForecast';
import { type Opportunity } from '../lib/crmService';
import { useBrand } from '../contexts/BrandContext';
import { useAuth } from '../contexts/AuthContext';

type View = 'calculator' | 'dashboard' | 'pipeline' | 'forecast';

export default function CRMApp() {
  const [activeView, setActiveView] = useState<View>('calculator');
  const [showOpportunityForm, setShowOpportunityForm] = useState(false);
  const [editingOpportunity, setEditingOpportunity] = useState<Opportunity | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { brands, selectedBrand, setSelectedBrand, selectedBrandIds, setSelectedBrandIds } = useBrand();
  const { profile } = useAuth();
  const [showBrandSelector, setShowBrandSelector] = useState(false);
  const isAdmin = profile?.role === 'admin';

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  useEffect(() => {
    if (!isAdmin && (activeView === 'dashboard' || activeView === 'forecast')) {
      setActiveView('calculator');
    }
  }, [isAdmin, activeView]);

  const handleCreateOpportunity = () => {
    setEditingOpportunity(null);
    setShowOpportunityForm(true);
  };

  const handleEditOpportunity = (opportunity: Opportunity) => {
    setEditingOpportunity(opportunity);
    setShowOpportunityForm(true);
  };

  const handleFormClose = () => {
    setShowOpportunityForm(false);
    setEditingOpportunity(null);
  };

  const handleFormSuccess = () => {
    setRefreshKey((prev) => prev + 1);
  };

  const navItems = [
    { id: 'dashboard' as View, label: 'Dashboard', icon: LayoutDashboard, adminOnly: true },
    { id: 'pipeline' as View, label: 'Pipeline', icon: Kanban, adminOnly: false },
    { id: 'calculator' as View, label: 'Calculator', icon: Calculator, adminOnly: false },
    { id: 'forecast' as View, label: 'Cash Flow Forecast', icon: TrendingUp, adminOnly: true },
  ].filter(item => !item.adminOnly || isAdmin);

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <aside
        className={`
          bg-gray-900 text-white flex-col transition-all duration-300 ease-in-out
          hidden md:flex
          ${sidebarCollapsed ? 'w-16' : 'w-64'}
        `}
      >
        <div className="p-4 flex items-center justify-center border-b border-gray-800">
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="p-1.5 hover:bg-gray-800 rounded transition-colors"
            aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {sidebarCollapsed ? <Menu className="w-5 h-5" /> : <X className="w-5 h-5" />}
          </button>
        </div>

        <nav className="flex-1 py-6">
          <div className={`${sidebarCollapsed ? 'px-2' : 'px-4'} mb-4`}>
            {!sidebarCollapsed && (
              <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                Navigation
              </h2>
            )}
            <div className="space-y-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = activeView === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => setActiveView(item.id)}
                    className={`
                      w-full flex items-center justify-between px-3 py-2.5 rounded-lg transition-all
                      ${
                        isActive
                          ? 'bg-gray-800 text-white'
                          : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                      }
                      ${sidebarCollapsed ? 'justify-center' : ''}
                    `}
                    title={sidebarCollapsed ? item.label : ''}
                  >
                    <div className="flex items-center space-x-3">
                      <Icon className="w-5 h-5 flex-shrink-0" />
                      {!sidebarCollapsed && (
                        <span className="font-medium text-sm">{item.label}</span>
                      )}
                    </div>
                    {!sidebarCollapsed && isActive && (
                      <ChevronRight className="w-4 h-4" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </nav>
      </aside>

      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      <aside
        className={`
          fixed top-0 left-0 bottom-0 w-64 bg-gray-900 text-white flex flex-col z-50
          transform transition-transform duration-300 ease-in-out md:hidden
          ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        <div className="p-4 flex items-center justify-between border-b border-gray-800">
          <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider">
            Navigation
          </h2>
          <button
            onClick={() => setMobileMenuOpen(false)}
            className="p-1.5 hover:bg-gray-800 rounded transition-colors"
            aria-label="Close menu"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="flex-1 py-6">
          <div className="px-4">
            <div className="space-y-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = activeView === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => {
                      setActiveView(item.id);
                      setMobileMenuOpen(false);
                    }}
                    className={`
                      w-full flex items-center justify-between px-3 py-3 rounded-lg transition-all
                      ${
                        isActive
                          ? 'bg-gray-800 text-white'
                          : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                      }
                    `}
                  >
                    <div className="flex items-center space-x-3">
                      <Icon className="w-5 h-5 flex-shrink-0" />
                      <span className="font-medium text-sm">{item.label}</span>
                    </div>
                    {isActive && <ChevronRight className="w-4 h-4" />}
                  </button>
                );
              })}
            </div>
          </div>
        </nav>
      </aside>

      <div className="flex-1 flex flex-col min-h-screen pb-16 md:pb-0">
        <header className="bg-white border-b border-gray-200 px-4 md:px-6 py-3 md:py-4 sticky top-0 z-30">
          <div className="flex items-center justify-between gap-4">
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors md:hidden"
              aria-label="Open menu"
            >
              <Menu className="w-5 h-5 text-gray-600" />
            </button>
            <h1 className="text-lg md:text-xl font-semibold text-gray-900 md:hidden">
              {navItems.find(item => item.id === activeView)?.label}
            </h1>
            <div className="flex-1"></div>
            {brands.length > 0 && (
              <div className="relative">
                <button
                  onClick={() => setShowBrandSelector(!showBrandSelector)}
                  className="flex items-center gap-2 px-3 md:px-4 py-2 bg-white border border-gray-300 rounded-lg hover:border-gray-400 transition-colors text-xs md:text-sm font-medium text-gray-900"
                >
                  <Building2 className="w-4 h-4 text-gray-600" />
                  <span className="hidden sm:inline">
                    {selectedBrandIds.length === 1
                      ? brands.find(b => b.id === selectedBrandIds[0])?.name
                      : `${selectedBrandIds.length} Brands`}
                  </span>
                  <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {showBrandSelector && (
                  <>
                    <div
                      className="fixed inset-0 z-30"
                      onClick={() => setShowBrandSelector(false)}
                    />
                    <div className="absolute right-0 mt-2 w-72 bg-white border border-gray-200 rounded-lg shadow-xl z-40">
                      <div className="p-3 border-b border-gray-200">
                        <p className="text-xs font-semibold text-gray-900">Select Brands</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {activeView === 'forecast' || activeView === 'pipeline' ? 'Choose 1 or more brands' : 'Single brand selection'}
                        </p>
                      </div>
                      <div className="p-2 max-h-64 overflow-y-auto space-y-1">
                        {brands.map(brand => {
                          const isSelected = selectedBrandIds.includes(brand.id);
                          const isMultiView = activeView === 'forecast' || activeView === 'pipeline';
                          const canSelect = isMultiView && (activeView === 'pipeline' || selectedBrandIds.length < 3);
                          const canDeselect = selectedBrandIds.length > 1;
                          return (
                            <button
                              key={brand.id}
                              onClick={() => {
                                if (isMultiView) {
                                  if (isSelected) {
                                    if (canDeselect) {
                                      const newIds = selectedBrandIds.filter(id => id !== brand.id);
                                      setSelectedBrandIds(newIds);
                                      const newBrand = brands.find(b => b.id === newIds[0]);
                                      if (newBrand) setSelectedBrand(newBrand);
                                    }
                                  } else if (canSelect) {
                                    setSelectedBrandIds([...selectedBrandIds, brand.id]);
                                  }
                                } else {
                                  setSelectedBrand(brand);
                                  setShowBrandSelector(false);
                                }
                              }}
                              disabled={isMultiView && isSelected && !canDeselect}
                              className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg transition-colors ${
                                isSelected
                                  ? 'bg-blue-50 text-blue-900'
                                  : 'hover:bg-gray-50 text-gray-900'
                              } ${
                                isMultiView && !isSelected && !canSelect
                                  ? 'opacity-50 cursor-not-allowed'
                                  : ''
                              }`}
                            >
                              <div className="flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: brand.primary_color }} />
                                <span className="text-sm font-medium">{brand.name}</span>
                              </div>
                              {isSelected && isMultiView && (
                                <div className="flex items-center gap-2">
                                  {selectedBrandIds.length > 1 && activeView === 'forecast' && (
                                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                                      {selectedBrandIds.indexOf(brand.id) + 1}
                                    </span>
                                  )}
                                  <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                </div>
                              )}
                              {isSelected && !isMultiView && (
                                <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </button>
                          );
                        })}
                      </div>
                      <div className="p-2 border-t border-gray-200">
                        <button
                          onClick={() => setShowBrandSelector(false)}
                          className="w-full px-3 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        >
                          Done
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </header>

        <main className="flex-1 overflow-auto">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 md:py-8">
            {activeView === 'calculator' && <DynamicCalculator />}
            {activeView === 'dashboard' && isAdmin && <Dashboard key={refreshKey} />}
            {activeView === 'pipeline' && (
              <Pipeline
                key={refreshKey}
                onCreateOpportunity={handleCreateOpportunity}
                onEditOpportunity={handleEditOpportunity}
              />
            )}
            {activeView === 'forecast' && isAdmin && <CashFlowForecast />}
          </div>
        </main>
      </div>

      <MobileNav activeView={activeView} onViewChange={setActiveView} />

      {showOpportunityForm && (
        <OpportunityForm
          opportunity={editingOpportunity}
          onClose={handleFormClose}
          onSuccess={handleFormSuccess}
        />
      )}
    </div>
  );
}
